import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { debounce, throttle } from 'lodash';
import ShortsCommentItem, { ShortsCommentData } from './CommentItem';
import ShortsVideoItem from './ShortsVideoItem';
import { useCommentList, useCommentReply, useMyInfo, useShortsMedia } from '../../../hooks';
import { dateYYYYMMDD } from '../../../utils/utils';
import commonFetcher from '../../../services/fetcher/common';
import SharePopup from '../../organisms/Popup/SharePopup';
import ProfileImage from '../../atoms/ProfileImage';
import MentionAddComment from '../../molecules/MentionComment/MentionAddComment';
import ShortsMediaEmojiComment from './ShortsMediaEmojiComment';
import MyInfoContext from '../../../contexts/MyInfoContext';
import { CommentPostType } from '../../../types/enum/CommentPostType';
import '../../../resource/css/shorts-scroll-snap.css';
import '../../../resource/css/shorts-comment-scroll.css';
import Paths from '../../../commons/paths';
import { WEB_RESOURCE_DOMAIN_URL } from '../../../commons/constants';
import { clearVolumeState } from '../../../utils/volumeStorage';

// CommentResDto를 ShortsCommentData로 변환하는 헬퍼 함수
const convertCommentToShortsCommentData = (comment: CommentResDto, userInfo?: any): ShortsCommentData => {
  return {
    ...comment,
    id: comment.commentId,
    type: comment.fixedYn === 'Y' ? 'fixed' : 'normal', // bestComment는 별도 배열에서 처리
    content: comment.contents,
    likes: comment.likeCnt,
    isLiked: comment.likeStatus === 'Y',
    isMine: comment.creatorId === userInfo?.userId, // 현재 사용자와 댓글 작성자 비교
    isModified: !!comment.modifiedDt,
    delUser: comment.delUser,
  };
};

/**
 * 숏폼 영상 목록 Component
 */
const ShortsMediaList = () => {
  const { shortsMediaId } = useParams<{ shortsMediaId?: string }>();
  const { info: myDetailInfo } = useMyInfo();

  const {
    isLoading,
    shortsMediaList,
    fetchShortsMediaList,
    fetchShortsMediaDetail,
    fetchShortsMediaDetailsBatch,
    shortsMediaIds,
    likeShortsMedia,
    getFullVersionContentsDetailInfo,
  } = useShortsMedia();

  const myInfoContext = useContext(MyInfoContext);
  const [userInfo, setUserInfo] = useState<any>();
  const [userDetailInfo, setUserDetailInfo] = useState<UserInfoResDto>();

  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [displayedVideos, setDisplayedVideos] = useState<ShortsMediaContentsDetailResDto[]>([]);
  const [fullVersionInfo, setFullVersionInfo] = useState<MediaDetailResDto | null>(null);
  const [isLoadingFullVersion, setIsLoadingFullVersion] = useState(false);
  const [hasMoreVideos, setHasMoreVideos] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [sharePopup, setSharePopup] = useState(false);
  const [isLoadingPinnedVideo, setIsLoadingPinnedVideo] = useState(false);
  const [pinnedShortsMediaDetailContents, setPinnedShortsMediaDetailContents] = useState<ShortsMediaContentsDetailResDto | null>(null);

  // 스크롤 상태 플래그
  const [isScrolling, setIsScrolling] = useState(false);
  
  // ✅ 빠른 스크롤 감지 및 프리로딩
  const [isFastScrolling, setIsFastScrolling] = useState(false);
  const lastVideoChangeTimeRef = useRef(Date.now());
  const previousVideoIndexRef = useRef(0);
  
  // ✅ 스크롤 패턴 추적 (역방향 후 재진입 감지)
  const maxReachedIndexRef = useRef(0);
  const wasAtEndRef = useRef(false);

  // ✅ 빠른 스크롤 감지 로직
  useEffect(() => {
    const now = Date.now();
    const timeDiff = now - lastVideoChangeTimeRef.current;
    const indexDiff = Math.abs(currentVideoIndex - previousVideoIndexRef.current);
    
    // 스크롤 패턴 추적 업데이트
    if (currentVideoIndex > maxReachedIndexRef.current) {
      maxReachedIndexRef.current = currentVideoIndex;
    }
    
    // 끝에 거의 도달했는지 체크 (전체의 90% 이상)
    const nearEndThreshold = Math.max(1, displayedVideos.length * 0.9);
    wasAtEndRef.current = currentVideoIndex >= nearEndThreshold;
    
    if (timeDiff > 0 && indexDiff > 0) {
      const scrollSpeed = indexDiff / timeDiff * 1000; // 초당 이동한 비디오 수
      
      // 1초에 1.5개 이상 비디오 이동 시 빠른 스크롤로 판단
      const newIsFastScrolling = scrollSpeed > 1.5;
      
      if (newIsFastScrolling !== isFastScrolling) {
        setIsFastScrolling(newIsFastScrolling);
        
        // 빠른 스크롤 시 추가 비디오 프리로드
        if (newIsFastScrolling && hasMoreVideos && !isLoadingMore) {
          const remainingVideos = displayedVideos.length - currentVideoIndex;
          if (remainingVideos < 12) { // 남은 비디오가 12개 미만이면 추가 로드 (10→12로 더 일찍)
            // loadMoreVideos는 나중에 정의되므로 setTimeout으로 지연 실행
            setTimeout(() => {
              loadMoreVideos().catch(error => {
                console.error('빠른 스크롤 중 추가 로드 실패:', error);
              });
            }, 0);
          }
        }
        
        // 일반 스크롤에서도 충분히 일찍 트리거
        if (!newIsFastScrolling && hasMoreVideos && !isLoadingMore) {
          const remainingVideos = displayedVideos.length - currentVideoIndex;
          if (remainingVideos <= 6) { // 일반 스크롤에서는 6개 남았을 때 로드
            setTimeout(() => {
              loadMoreVideos().catch(error => {
                console.error('일반 스크롤 중 추가 로드 실패:', error);
              });
            }, 0);
          }
        }
      }
    }
    
    lastVideoChangeTimeRef.current = now;
    previousVideoIndexRef.current = currentVideoIndex;
  }, [currentVideoIndex, isFastScrolling, hasMoreVideos, isLoadingMore, displayedVideos.length]);

  // 트랙패드 스크롤 누적/리셋용 Ref 및 상수
  const wheelAccumRef = useRef(0);
  const wheelLastTimeRef = useRef(0);
  const WHEEL_TRIGGER_PX = 80; // 트랙패드에서 한/두 번 쓸어내릴 때 누적 임계값
  const WHEEL_RESET_MS = 250;  // 이 시간 동안 입력이 없으면 누적값 리셋

  /* *****************************************
  * Ref 영역
   * *****************************************/
  const titleAreaRef = useRef<HTMLDivElement>(null);

  const videoContainerRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<(HTMLLIElement | null)[]>([]);
  const displayedVideosRef = useRef<ShortsMediaContentsDetailResDto[]>([]);

  // 숏폼 우측 풀영상/댓글 영역 Container ref
  const rightContainerRef = useRef<HTMLDivElement>(null);

  // 댓글 입력 ref
  const cmtDivRef = useRef<HTMLDivElement>(null);
  const cmtInputRef = useRef<HTMLDivElement>(null);

  // 풀영상 버전 Ref
  const fullVersionMediaRef = useRef<HTMLAnchorElement>(null);

  // 최초 URL 저장 (경쟁 조건 방지)
  const initialUrlRef = useRef<string>('');
  const isPinnedVideoProcessedRef = useRef<boolean>(false);
  // 컴포넌트 마운트시 데이터 로딩
  const isInitialLoadRef = useRef<boolean>(true);

  // 이모지 관련 상태
  const [commentContent, setCommentContent] = useState('');

  // 답글 관련 상태
  const [selectedCommentId, setSelectedCommentId] = useState<string>('');
  const [replyLists, setReplyLists] = useState<{[commentId: string]: CommentResDto[]}>({});
  const [isReplyInputOpen, setIsReplyInputOpen] = useState<{[commentId: string]: boolean}>({});
  const [isReplyOpen, setIsReplyOpen] = useState<{[commentId: string]: boolean}>({});

  // 페이징 상수 - 빠른 스크롤 대응을 위해 증가
  const INITIAL_LOAD_COUNT = 8; // 초기 로드 개수 (5→8)
  const LOAD_MORE_COUNT = 8; // 추가 로드 개수 (5→8)  
  const LOAD_MORE_TRIGGER_INDEX = 3; // 4번째 영상에서 추가 로드 트리거 (5→3으로 더 일찍)

  const mediaThumbImageUri = useMemo(() => {
    if (!fullVersionInfo || !fullVersionInfo.thumbImageUri) {
      return '/assets/images/common/no-image.png';
    }

    const thumbUri = fullVersionInfo.thumbImageUri as string;

    if (thumbUri.includes('http') || thumbUri.includes('https')) {
      return thumbUri;
    }

    return window.location.hostname.includes('localhost')
      ? WEB_RESOURCE_DOMAIN_URL.concat(thumbUri)
      : thumbUri;
  }, [fullVersionInfo]);

  // 조회수 증가 함수 - 최적화된 버전
  const incrementReadCount = useCallback(async (video: ShortsMediaContentsDetailResDto) => {
    try {
      if (!video?.shortsMediaId) {
        return;
      }

      const countReq: CountAddReqDto = {
        contentsId: video.shortsMediaId,
        contentsPostType: 'SHORTS_MEDIA'
      };

      await commonFetcher.putReadCount(countReq);
    } catch (error) {
      console.error('조회수 증가 실패:', error);
    }
  }, []);

  // 현재 비디오 변경 시 조회수 증가

  useEffect(() => {
    const currentVideo = displayedVideos[currentVideoIndex];
    if (currentVideo && displayedVideos.length > 0) {
      // 비디오가 실제로 보여지고 있을 때만 조회수 증가
      incrementReadCount(currentVideo);
    }
  }, [currentVideoIndex, displayedVideos, incrementReadCount]);

  // 영상 목록 정렬 함수 - 특정 shortsMediaId를 최상단에 배치
  const sortVideosByPriority = useCallback((videoList: ShortsMediaContentsDetailResDto[]) => {
    if (!shortsMediaId || !videoList.length) {
      return videoList;
    }

    // shortsMediaId가 매칭되는 영상 찾기
    const targetVideoIndex = videoList.findIndex(video =>
      video.shortsMediaId === shortsMediaId
    );

    if (targetVideoIndex === -1) {
      return videoList;
    }

    // 매칭되는 영상을 제거하고 별도로 저장
    const targetVideo = videoList[targetVideoIndex];
    const otherVideos = videoList.filter((_, index) => index !== targetVideoIndex);

    // 최종 순서: 매칭 영상 -> 나머지 영상 (고정 영상은 useShortsMedia 훅에서 이미 처리됨)
    return [targetVideo, ...otherVideos];
  }, [shortsMediaId]);

  // 더 많은 비디오 로드하는 함수 - 깜박임 방지 최적화
  const loadMoreVideos = useCallback(async () => {
    if (isLoadingMore || !hasMoreVideos || !shortsMediaIds) return;
    
    // 중복 호출 방지를 위한 추가 체크
    const currentCount = displayedVideos.length;
    const totalCount = shortsMediaIds.length;
    if (currentCount >= totalCount) {
      setHasMoreVideos(false);
      return;
    }
    
    setIsLoadingMore(true);

    try {
      // 현재 스크롤 위치와 컨테이너 정보 저장
      const scrollContainer = videoContainerRef.current;
      const currentScrollTop = scrollContainer?.scrollTop || 0;
      const containerHeight = scrollContainer?.clientHeight || 0;

      // 현재 표시된 비디오 개수
      const currentCount = displayedVideos.length;
      const totalCount = shortsMediaIds.length;

      // 다음에 로드할 비디오 ID들
      const nextIds = shortsMediaIds.slice(currentCount, currentCount + LOAD_MORE_COUNT);

      // 배치로 상세 정보 로드
      const nextVideos = await fetchShortsMediaDetailsBatch(nextIds);

      if (nextVideos.length > 0) {
        // 중복 제거: 기존 배열에 있는 shortsMediaId는 제외하고 추가
        const existingIds = new Set(displayedVideos.map(video => video.shortsMediaId));
        const uniqueNewVideos = nextVideos.filter(video => !existingIds.has(video.shortsMediaId));

        if (uniqueNewVideos.length > 0) {
          // 한 번에 상태 업데이트하여 깜박임 최소화
          setDisplayedVideos(prev => {
            const newVideos = [...prev, ...uniqueNewVideos];
            return newVideos;
          });

          // 스크롤 위치 즉시 복원 (깜박임 방지)
          if (scrollContainer && currentScrollTop > 0) {
            // 미세한 지연으로 DOM 업데이트 후 스크롤 복원
            requestAnimationFrame(() => {
              scrollContainer.scrollTop = currentScrollTop;
            });
          }
        }

        setHasMoreVideos(currentCount + LOAD_MORE_COUNT < totalCount);
      } else {
        setHasMoreVideos(false);
      }
    } catch (error) {
      console.error('추가 비디오 로드 실패:', error);
      setHasMoreVideos(false);
    } finally {
      // 로딩 상태 해제를 더 빠르게 처리
      setTimeout(() => {
        setIsLoadingMore(false);
      }, 100); // 300ms에서 100ms로 단축
    }
  }, [shortsMediaIds, displayedVideos, isLoadingMore, hasMoreVideos, LOAD_MORE_COUNT, fetchShortsMediaDetailsBatch]);

  // Intersection Observer 설정 - 깜박임 방지 최적화
  useEffect(() => {
    if (displayedVideos.length === 0) return;

    const options = {
      root: videoContainerRef.current,
      rootMargin: '0px',
      threshold: [0.25, 0.5, 0.75, 1],
    };

    let rafId: number | null = null;

    const observer = new IntersectionObserver((entries) => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        let bestIndex = -1;
        let bestRatio = 0;

        entries.forEach((entry) => {
          try {
            // entry.target이 유효하고 아직 DOM에 연결되어 있는지 확인
            if (entry.target && entry.target?.parentNode && entry.target?.isConnected) {
              const idx = parseInt(entry.target.getAttribute('data-index') || '-1', 10);
              if (idx >= 0 && entry.intersectionRatio > bestRatio) {
                bestRatio = entry.intersectionRatio;
                bestIndex = idx;
              }
            }
          } catch (error) {
            // DOM 요소 접근 오류 무시
          }
        });

        if (bestIndex >= 0 && bestIndex !== currentVideoIndex) {
          setCurrentVideoIndex(bestIndex);

          const currentVideoId = displayedVideos[bestIndex]?.shortsMediaId;
          if (currentVideoId) {
            const newPath = `${Paths.getPath('shortsMediaDetail').replace(':shortsMediaId', currentVideoId)}`;
            window.history.replaceState(null, '', newPath);
          }

          // 무한 스크롤 트리거 로직 개선 - 더 일찍, 더 자주 체크
          if (hasMoreVideos && !isLoadingMore) {
            const remainingVideos = displayedVideos.length - bestIndex;
            // 60% 지점이나 6개 중 더 작은 값으로 더 일찍 트리거
            let triggerPoint = Math.min(6, Math.max(3, displayedVideos.length * 0.6));
            
            // 역방향 후 재진입 패턴 감지 - 더욱 적극적으로 로드
            const isReturningToEnd = bestIndex >= maxReachedIndexRef.current * 0.8 && wasAtEndRef.current;
            if (isReturningToEnd) {
              triggerPoint = Math.min(8, triggerPoint + 2); // 더 일찍 트리거
            }
            
            if (remainingVideos <= triggerPoint) {
              loadMoreVideos().catch(error => {
                console.error('무한 스크롤 중 추가 로드 실패:', error);
              });
            }
          }
        }
      });
    }, options);

    const currentRefs = videoRefs.current.filter((ref) => ref !== null) as HTMLLIElement[];

    currentRefs.forEach((ref) => observer.observe(ref));

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      currentRefs.forEach((ref) => observer.unobserve(ref));
      observer.disconnect();
    };
  }, [displayedVideos, currentVideoIndex]);

  // currentVideoIndex 바뀔 때 나머지 영상 일괄 정지
  useEffect(() => {
    const all = Array.from(document.querySelectorAll<HTMLVideoElement>('video[data-short-id]'));
    all.forEach((el, i) => {
      try {
        if (i !== currentVideoIndex && el && !el.paused) el.pause();
      } catch (error) {
        // null pointer 오류 방지 - 무시하고 계속 진행
      }
    });
  }, [currentVideoIndex]);

  // 특정 비디오로 스크롤하는 함수 - 부드러운 스크롤 + 중복 실행 방지
  const scrollToVideo = useCallback((index: number, forceScroll = false) => {
    // forceScroll이 true가 아니고 이미 스크롤 진행 중이면 중복 실행 방지
    if (!forceScroll && isScrolling) {
      return;
    }

    const targetRef = videoRefs.current[index];
    if (targetRef && videoContainerRef.current) {
      setIsScrolling(true); // 스크롤 시작

      // 더 부드러운 스크롤을 위해 여러 옵션 시도
      try {
        // 먼저 videoContainerRef에서 스크롤 시도
        targetRef.scrollIntoView({
          behavior: 'smooth',
          block: 'center' // start에서 center로 변경하여 더 부드럽게
        });
      } catch (error) {
        // 실패 시 content div에서 스크롤 시도
        try {
          const contentDiv = document.querySelector('.shorts.content');
          if (contentDiv && contentDiv.isConnected && targetRef && targetRef.isConnected) {
            targetRef.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        } catch (err) {
          console.warn('스크롤 처리 중 오류:', err);
        }
      }

      // 스크롤 완료 대기 (smooth scroll 시간 고려)
      setTimeout(() => {
        setIsScrolling(false); // 스크롤 완료
      }, 500); // 부드러운 스크롤 완료 시간
    }
  }, [isScrolling]);

  // 키보드 이벤트 처리 - useCallback으로 최적화 + 중복 실행 방지
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!displayedVideos.length || isScrolling) return; // 스크롤 진행 중이면 무시

    // input, textarea, contenteditable 요소에서는 키보드 이벤트 무시
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    const totalDisplayedVideos = displayedVideos.length;
    let newIndex = currentVideoIndex;

    switch (event.key) {
      case 'ArrowUp':
      case 'PageUp':
        event.preventDefault();
        newIndex = Math.max(0, currentVideoIndex - 1);
        break;
      case 'ArrowDown':
      case 'PageDown':
        event.preventDefault();
        newIndex = Math.min(totalDisplayedVideos - 1, currentVideoIndex + 1);
        break;
      default:
        return;
    }

    if (newIndex !== currentVideoIndex) {
      scrollToVideo(newIndex);

      // 새 비디오의 shortsMediaId로 URL 업데이트 (페이지 이동 없이)
      const newVideoId = displayedVideos[newIndex]?.shortsMediaId;
      if (newVideoId) {
        const newPath = `${Paths.getPath('shortsMediaDetail').replace(':shortsMediaId',newVideoId)}`;
        window.history.replaceState(null, '', newPath);
      }
    }
  }, [currentVideoIndex, displayedVideos, scrollToVideo, isScrolling]);

  // Debounced 키보드 이벤트 핸들러 (연속 키 입력 제어)
  const debouncedHandleKeyDown = useMemo(
    () => debounce(handleKeyDown, 150), // 150ms 지연
    [handleKeyDown]
  );

  // 비디오 종료 시 다음 영상으로 자동 이동 (스크롤 플래그 영향 받지 않음)
  const handleVideoEnd = useCallback((currentIndex: number) => {
    if (!displayedVideos.length) return;

    const totalDisplayedVideos = displayedVideos.length;
    const nextIndex = currentIndex + 1;

    // 다음 영상이 있으면 이동 (영상 자동 전환은 스크롤 플래그와 무관하므로 강제 실행)
    if (nextIndex < totalDisplayedVideos) {

      // 강제로 다음 영상으로 스크롤 (forceScroll=true)
      scrollToVideo(nextIndex, true);

      // 다음 비디오의 shortsMediaId로 URL 업데이트 (페이지 이동 없이)
      const nextVideoId = displayedVideos[nextIndex]?.shortsMediaId;
      if (nextVideoId) {
        const newPath = `${Paths.getPath('shortsMediaDetail').replace(':shortsMediaId',nextVideoId)}`;
        window.history.replaceState(null, '', newPath);
      }
    } else {
        // 🎬 마지막 영상 도달, 자동 전환 없음
    }
  }, [displayedVideos, scrollToVideo]);

  // 휠 이벤트로 스크롤 제어 - 누적(delta) 방식으로 트랙패드 제스처 처리
  const handleWheel = useCallback((event: WheelEvent) => {
    if (!displayedVideos.length || isScrolling) return; // 스크롤 진행 중이면 무시

    // 댓글/입력 영역에서의 스크롤은 무시
    const target = event.target as Node;
    try {
      const commentArea = document.querySelector('.detail_right_comment');
      const commentScrollArea = document.querySelector('.detail_comment_list');
      const commentBoxArea = document.querySelector('.comment_box');
      const sharePopupArea = document.querySelector('.share_popup');
      if (
        (commentArea && target && commentArea.isConnected && (commentArea === target || commentArea.contains(target))) ||
        (commentScrollArea && target && commentScrollArea.isConnected && (commentScrollArea === target || commentScrollArea.contains(target))) ||
        (commentBoxArea && target && commentBoxArea.isConnected && (commentBoxArea === target || commentBoxArea.contains(target))) ||
        (sharePopupArea && target && sharePopupArea.isConnected && (sharePopupArea === target || sharePopupArea.contains(target)))
      ) {
        return;
      }
    } catch (error) {
      // DOM 요소 접근 오류 무시
    }

    // 기본 스크롤 동작을 막고 우리가 제어 (trackpad 지원을 위해 반드시 passive:false로 등록되어야 함)
    event.preventDefault();

    // 수평 스크롤이 우세하면 무시 (트랙패드 좌/우 제스처 방지)
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      return;
    }

    // delta 값 정규화 (pixels 기준)
    let deltaY = event.deltaY;
    // deltaMode: 0=pixel, 1=line, 2=page
    if ((event as any).deltaMode === 1) {
      deltaY *= 16; // line을 대략 16px로 환산
    } else if ((event as any).deltaMode === 2) {
      deltaY *= window.innerHeight; // page를 viewport 높이로 환산
    }

    const now = Date.now();
    // 일정 시간 입력이 없으면 누적값 리셋
    if (now - wheelLastTimeRef.current > WHEEL_RESET_MS) {
      wheelAccumRef.current = 0;
    }
    wheelLastTimeRef.current = now;

    // 누적
    wheelAccumRef.current += deltaY;

    // 임계치 도달 시 이전/다음 영상으로 이동
    if (Math.abs(wheelAccumRef.current) >= WHEEL_TRIGGER_PX) {
      const goingDown = wheelAccumRef.current > 0;
      wheelAccumRef.current = 0; // 트리거 후 누적값 초기화

      const totalDisplayedVideos = displayedVideos.length;
      let newIndex = currentVideoIndex;

      if (goingDown) {
        // 아래로 스크롤 (다음 영상)
        newIndex = Math.min(totalDisplayedVideos - 1, currentVideoIndex + 1);
      } else {
        // 위로 스크롤 (이전 영상)
        newIndex = Math.max(0, currentVideoIndex - 1);
      }

      if (newIndex !== currentVideoIndex) {
        scrollToVideo(newIndex);

        // 새 비디오의 shortsMediaId로 URL 업데이트 (페이지 이동 없이)
        const newVideoId = displayedVideos[newIndex]?.shortsMediaId;
        if (newVideoId) {
          const newPath = `${Paths.getPath('shortsMediaDetail').replace(':shortsMediaId', newVideoId)}`;
          window.history.replaceState(null, '', newPath);
        }
      }
    }
  }, [currentVideoIndex, displayedVideos, scrollToVideo, isScrolling]);

  // Throttled 휠 이벤트 핸들러 (더 부드럽게 트랙패드 지원)
  const debouncedHandleWheel = useMemo(
    () => throttle(handleWheel, 50, { leading: true, trailing: true }),
    [handleWheel]
  );

  // EventListener 호환 wrapper 함수들

  useEffect(() => {
    const wheelHandler = (event: Event) => {
      // 먼저 댓글 영역 체크 (preventDefault 호출 전에!)
      const target = event.target as Node;
      try {
        const commentArea = document.querySelector('.detail_right_comment');
        const commentScrollArea = document.querySelector('.detail_comment_list');
        const commentBoxArea = document.querySelector('.comment_box');
        const sharePopupArea = document.querySelector('.share_popup');
        
        if ((commentArea && target && commentArea.isConnected && (commentArea === target || commentArea.contains(target))) ||
            (commentScrollArea && target && commentScrollArea.isConnected && (commentScrollArea === target || commentScrollArea.contains(target))) ||
            (commentBoxArea && target && commentBoxArea.isConnected && (commentBoxArea === target || commentBoxArea.contains(target))) ||
            (sharePopupArea && target && sharePopupArea.isConnected && (sharePopupArea === target || sharePopupArea.contains(target)))
        ) {
          // 댓글 영역에서는 아무것도 하지 않고 기본 스크롤 동작 허용
          return;
        }
      } catch (error) {
        // DOM 요소 접근 오류 무시
      }

      // 댓글 영역이 아닌 경우에만 기본 동작 차단
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      debouncedHandleWheel(event as WheelEvent);
    };

    const keyHandler = (event: Event) => {
      // 숏폼 페이지가 아니거나 숏폼 영역이 아니면 키보드 이벤트 무시
      try {
        const contentShortsElement = document.querySelector('.content.shorts');
        if (!contentShortsElement || !contentShortsElement.isConnected) {
          return;
        }
      } catch (error) {
        // DOM 요소 접근 오류 시 무시
        return;
      }

      // input, textarea, contenteditable 등에서는 키보드 이벤트 무시 (기존과 동일)
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      debouncedHandleKeyDown(event as KeyboardEvent);
    };

    // ShortsMedia 영역에만 휠 이벤트 리스너 등록 (범위 축소)
    const scrollContainer = videoContainerRef.current; // 실제 영상 스크롤 컨테이너
    let contentShortsElement: Element | null = null;
    let shortsLeftArea: Element | null = null;
    
    try {
      contentShortsElement = document.querySelector('.content.shorts'); // 숏폼 컨테이너
      shortsLeftArea = document.querySelector('.detail_left'); // 좌측 영상 영역
    } catch (error) {
      console.warn('DOM 요소 탐색 중 오류:', error);
    }
    
    // LNB나 다른 영역은 제외하고 오직 ShortsMedia 관련 영역에만 제한
    const containers = [scrollContainer, contentShortsElement, shortsLeftArea]
      .filter((container): container is Element => container !== null && container.isConnected);

    containers.forEach((container, index) => {
      // capture 단계에서 이벤트를 가로채서 더 빠르게 처리
      container.addEventListener('wheel', wheelHandler, { passive: false, capture: true });
    });

    // 키보드 이벤트는 숏폼 영역에만 등록 (body, document 제외)
    if (contentShortsElement) {
      contentShortsElement.addEventListener('keydown', keyHandler);
    }
    // 대안으로 document에 등록하되 조건 체크 추가
    document.addEventListener('keydown', keyHandler);

    return () => {
      // ✅ 모든 이벤트 리스너 명시적 제거
      containers.forEach(container => {
        if (container && container.removeEventListener) {
          container.removeEventListener('wheel', wheelHandler, { capture: true } as any);
        }
      });
      
      if (contentShortsElement && contentShortsElement.removeEventListener) {
        contentShortsElement.removeEventListener('keydown', keyHandler);
      }
      
      if (document.removeEventListener) {
        document.removeEventListener('keydown', keyHandler);
      }
      
      // cleanup: debounce 함수 취소
      debouncedHandleWheel.cancel();
      debouncedHandleKeyDown.cancel();
    };
  }, [debouncedHandleWheel, debouncedHandleKeyDown]);

  // 터치/마우스 이벤트로 스크롤 제어 - useCallback으로 최적화
  const handleTouchStart = useCallback((event: TouchEvent) => {
    if (!displayedVideos.length) return;

    // 댓글 영역에서의 터치는 무시 (더 광범위하게)
    const target = event.target as Node;
    try {
      const commentArea = document.querySelector('.detail_right_comment');
      const commentScrollArea = document.querySelector('.detail_comment_list');
      const sharePopupArea = document.querySelector('.share_popup');
      if ((commentArea && target && commentArea.isConnected && (commentArea === target || commentArea.contains(target))) ||
          (commentScrollArea && target && commentScrollArea.isConnected && (commentScrollArea === target || commentScrollArea.contains(target))) ||
         (sharePopupArea && target && sharePopupArea.isConnected && (sharePopupArea === target || sharePopupArea.contains(target)))
      ) {
        return; // 댓글 영역 내에서의 터치는 기본 동작 유지
      }
    } catch (error) {
      // DOM 요소 접근 오류 무시
    }

    const startY = event.touches[0].clientY;
    const startTime = Date.now();

    // 이벤트 객체에 데이터 저장
    (event as any).startY = startY;
    (event as any).startTime = startTime;

  }, [displayedVideos.length]);

  const handleMouseDown = useCallback((event: MouseEvent) => {
    if (!displayedVideos.length) return;

    // 댓글 영역에서의 마우스 다운은 무시 (더 광범위하게)
    const target = event.target as Node;
    try {
      const commentArea = document.querySelector('.detail_right_comment');
      const commentScrollArea = document.querySelector('.detail_comment_list');
      const sharePopupArea = document.querySelector('.share_popup');
      if ((commentArea && target && commentArea.isConnected && (commentArea === target || commentArea.contains(target))) ||
          (commentScrollArea && target && commentScrollArea.isConnected && (commentScrollArea === target || commentScrollArea.contains(target))) ||
          (sharePopupArea && target && sharePopupArea.isConnected && (sharePopupArea === target || sharePopupArea.contains(target)))
      ) {
        return; // 댓글 영역 내에서의 마우스 다운은 기본 동작 유지
      }
    } catch (error) {
      // DOM 요소 접근 오류 무시
    }

    const startY = event.clientY;
    const startTime = Date.now();

    // 이벤트 객체에 데이터 저장
    (event as any).startY = startY;
    (event as any).startTime = startTime;
  }, [displayedVideos.length]);

  const handleTouchEnd = useCallback((event: TouchEvent) => {
    if (!displayedVideos.length || isScrolling) return; // 스크롤 진행 중이면 무시

    // 댓글 영역에서의 터치 종료는 무시 (더 광범위하게)
    const target = event.target as Node;
    try {
      const commentArea = document.querySelector('.detail_right_comment');
      const commentScrollArea = document.querySelector('.detail_comment_list');
      const sharePopupArea = document.querySelector('.share_popup');
      if ((commentArea && target && commentArea.isConnected && (commentArea === target || commentArea.contains(target))) ||
          (commentScrollArea && target && commentScrollArea.isConnected && (commentScrollArea === target || commentScrollArea.contains(target))) ||
          (sharePopupArea && target && sharePopupArea.isConnected && (sharePopupArea === target || sharePopupArea.contains(target)))
      ) {
        return; // 댓글 영역 내에서의 터치 종료는 기본 동작 유지
      }
    } catch (error) {
      // DOM 요소 접근 오류 무시
    }

    const startY = (event as any).startY;
    const startTime = (event as any).startTime;

    if (!startY || !startTime) return;

    const endY = event.changedTouches[0].clientY;
    const deltaY = startY - endY;
    const deltaTime = Date.now() - startTime;

    // 최소 스와이프 거리와 시간 체크
    if (Math.abs(deltaY) > 50 && deltaTime < 500) {
      const totalDisplayedVideos = displayedVideos.length;
      let newIndex = currentVideoIndex;

      if (deltaY > 0) {
        // 위로 스와이프 (다음 영상)
        newIndex = Math.min(totalDisplayedVideos - 1, currentVideoIndex + 1);
      } else {
        // 아래로 스와이프 (이전 영상)
        newIndex = Math.max(0, currentVideoIndex - 1);
      }

      if (newIndex !== currentVideoIndex) {
        scrollToVideo(newIndex);
      }
    }
  }, [currentVideoIndex, displayedVideos, scrollToVideo, isScrolling]);

  // Debounced 터치 이벤트 핸들러 (연속 터치 스와이프 제어)
  const debouncedHandleTouchEnd = useMemo(
    () => debounce(handleTouchEnd, 200), // 200ms 지연
    [handleTouchEnd]
  );

  const handleMouseUp = useCallback((event: MouseEvent) => {
    if (!displayedVideos.length || isScrolling) return; // 스크롤 진행 중이면 무시

    // 댓글 영역에서의 마우스 업은 무시 (더 광범위하게)
    const target = event.target as Node;
    try {
      const commentArea = document.querySelector('.detail_right_comment');
      const commentScrollArea = document.querySelector('.detail_comment_list');
      const sharePopupArea = document.querySelector('.share_popup');
      if ((commentArea && target && commentArea.isConnected && (commentArea === target || commentArea.contains(target))) ||
          (commentScrollArea && target && commentScrollArea.isConnected && (commentScrollArea === target || commentScrollArea.contains(target))) ||
          (sharePopupArea && target && sharePopupArea.isConnected && (sharePopupArea === target || sharePopupArea.contains(target)))
      ) {
        return; // 댓글 영역 내에서의 마우스 업은 기본 동작 유지
      }
    } catch (error) {
      // DOM 요소 접근 오류 무시
    }

    const startY = (event as any).startY;
    const startTime = (event as any).startTime;

    if (!startY || !startTime) return;

    const endY = event.clientY;
    const deltaY = startY - endY;
    const deltaTime = Date.now() - startTime;

    // 최소 스와이프 거리와 시간 체크
    if (Math.abs(deltaY) > 50 && deltaTime < 500) {
      const totalDisplayedVideos = displayedVideos.length;
      let newIndex = currentVideoIndex;

      if (deltaY > 0) {
        // 위로 스와이프 (다음 영상)
        newIndex = Math.min(totalDisplayedVideos - 1, currentVideoIndex + 1);
      } else {
        // 아래로 스와이프 (이전 영상)
        newIndex = Math.max(0, currentVideoIndex - 1);
      }

      if (newIndex !== currentVideoIndex) {
        scrollToVideo(newIndex);

        // 새 비디오의 shortsMediaId로 URL 업데이트 (페이지 이동 없이)
        const newVideoId = displayedVideos[newIndex]?.shortsMediaId;
        if (newVideoId) {
          const newPath = `${Paths.getPath('shortsMediaDetail').replace(':shortsMediaId',newVideoId)}`;
          window.history.replaceState(null, '', newPath);
        }
      }
    }
  }, [currentVideoIndex, displayedVideos, scrollToVideo, isScrolling]);

  // Debounced 마우스 이벤트 핸들러 (연속 마우스 드래그 제어)
  const debouncedHandleMouseUp = useMemo(
    () => debounce(handleMouseUp, 200), // 200ms 지연
    [handleMouseUp]
  );

  // EventListener 호환 wrapper 함수들 정의
  const touchStartEventListener = useCallback((event: Event) => {
    handleTouchStart(event as TouchEvent);
  }, [handleTouchStart]);

  const touchEndEventListener = useCallback((event: Event) => {
    debouncedHandleTouchEnd(event as TouchEvent);
  }, [debouncedHandleTouchEnd]);

  const mouseDownEventListener = useCallback((event: Event) => {
    handleMouseDown(event as MouseEvent);
  }, [handleMouseDown]);

  const mouseUpEventListener = useCallback((event: Event) => {
    debouncedHandleMouseUp(event as MouseEvent);
  }, [debouncedHandleMouseUp]);

  useEffect(() => {
    // ShortsMedia 영역에만 터치/마우스 이벤트 리스너 추가 (범위 축소)
    const scrollContainer = videoContainerRef.current; // 실제 영상 스크롤 컨테이너
    let contentShortsElement: Element | null = null;
    let shortsLeftArea: Element | null = null;
    
    try {
      contentShortsElement = document.querySelector('.content.shorts'); // 숏폼 컨테이너
      shortsLeftArea = document.querySelector('.detail_left'); // 좌측 영상 영역
    } catch (error) {
      console.warn('터치/마우스 이벤트 DOM 요소 탐색 중 오류:', error);
    }
    
    // LNB나 다른 영역은 제외하고 오직 ShortsMedia 관련 영역에만 제한
    const containers = [scrollContainer, contentShortsElement, shortsLeftArea]
      .filter((container): container is Element => container !== null && container.isConnected);

    // 댓글 영역은 제외
    let commentArea: Element | null = null;
    try {
      commentArea = document.querySelector('.detail_right_comment');
    } catch (error) {
      console.warn('댓글 영역 탐색 중 오류:', error);
    }

    containers.forEach((container, index) => {
      // 댓글 영역이 아닌 곳에만 이벤트 리스너 추가
      if (commentArea !== container && !commentArea?.contains(container)) {
        // 터치 이벤트
        container.addEventListener('touchstart', touchStartEventListener, { passive: true });
        container.addEventListener('touchend', touchEndEventListener, { passive: true });

        // 마우스 이벤트
        container.addEventListener('mousedown', mouseDownEventListener, { passive: true });
        container.addEventListener('mouseup', mouseUpEventListener, { passive: true });
      }
    });

    return () => {
      // ✅ 더 안전한 이벤트 리스너 제거
      containers.forEach(container => {
        // 댓글 영역이 아닌 곳에서만 이벤트 리스너 제거
        if (commentArea !== container && !commentArea?.contains(container)) {
          if (container && container.removeEventListener) {
            container.removeEventListener('touchstart', touchStartEventListener);
            container.removeEventListener('touchend', touchEndEventListener);
            container.removeEventListener('mousedown', mouseDownEventListener);
            container.removeEventListener('mouseup', mouseUpEventListener);
          }
        }
      });
      // cleanup: debounce 함수들 취소
      debouncedHandleTouchEnd.cancel();
      debouncedHandleMouseUp.cancel();
    };
  }, [touchStartEventListener, touchEndEventListener, mouseDownEventListener, mouseUpEventListener, debouncedHandleTouchEnd, debouncedHandleMouseUp]);

  // 컴포넌트 언마운트 시 모든 비디오 정지 및 debounce 함수 정리

  useEffect(() => {
    return () => {
      // // 메모리 모니터링 정리 (개발환경)
      // if (process.env.NODE_ENV === 'development' && (window as any).shortsMemoryCleanup) {
      //   (window as any).shortsMemoryCleanup();
      //   delete (window as any).shortsMemoryCleanup;
      // }
      
      // ✅ 안전한 비디오 리소스 해제 (DOM은 유지)
      try {
        const videoElements = document.querySelectorAll('video');
        
        videoElements.forEach((video, index) => {
          try {
            // 1. 재생 중지
            if (!video.paused) {
              video.pause();
            }
            
            // 2. 위치 초기화
            try {
              if ('currentTime' in video) {
                video.currentTime = 0;
              }
            } catch {}
            
            // 3. 소스 해제 (DOM은 유지)
            video.removeAttribute('src');
            video.src = '';
            
            // 4. source 태그들도 제거
            try {
              const sources = video.querySelectorAll('source');
              sources.forEach(source => {
                try {
                  if (source && source?.parentNode && source?.isConnected) {
                    source.remove();
                  }
                } catch (error) {
                  // source 태그 제거 중 오류 무시
                }
              });
            } catch (error) {
              // source 태그 탐색 중 오류 무시
            }
            
            // 5. 리소스 해제
            video.load();
          } catch (error) {
            console.warn(`❌ 비디오 ${index + 1} 리소스 해제 실패:`, error);
          }
        });
      } catch (error) {
        console.warn('비디오 리소스 해제 중 오류:', error);
      }

      // 모든 debounce 함수들 정리
      try {
        debouncedHandleKeyDown.cancel();
        debouncedHandleWheel.cancel();
        debouncedHandleTouchEnd.cancel();
        debouncedHandleMouseUp.cancel();
      } catch (error) {
        console.warn('Debounce 함수 정리 중 오류:', error);
      }
      
      // ✅ 상태 초기화 및 참조 정리 (컴포넌트 내부만)
      setDisplayedVideos([]);
      setCurrentVideoIndex(0);
      setHasMoreVideos(true);
      setIsLoadingMore(false);
      setSharePopup(false);
      setPinnedShortsMediaDetailContents(null);
      setFullVersionInfo(null);
      setIsLoadingPinnedVideo(false);
      
      // 컴포넌트 내부 ref들 초기화
      videoRefs.current = [];
      displayedVideosRef.current = [];
      wheelAccumRef.current = 0;
      wheelLastTimeRef.current = 0;
      isPinnedVideoProcessedRef.current = false;
      isInitialLoadRef.current = true;
    };
  }, []); // 빈 배열로 변경 - 컴포넌트 언마운트 시에만 실행

  // 페이지 이탈 시 모든 비디오 정지 및 볼륨 상태 초기화

  useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        // ✅ 안전한 비디오 리소스 해제 (DOM은 유지)
        const videoElements = document.querySelectorAll('video');
        
        videoElements.forEach((video, index) => {
          try {
            video.pause();
            try {
              if ('currentTime' in video) {
                video.currentTime = 0;
              }
            } catch {}
            video.removeAttribute('src');
            video.src = '';
            video.load();
          } catch (error) {
            console.warn(`페이지 이탈 시 비디오 ${index} 리소스 해제 실패:`, error);
          }
        });

        // 페이지 이탈 시 볼륨 상태 초기화
        clearVolumeState();
        
        // 강제 가비지 컬렉션
        if ((window as any).gc) {
          (window as any).gc();
        }

      } catch (error) {
        console.warn('페이지 이탈 시 비디오 정지 중 오류:', error);
      }
    };

    const handleVisibilityChange = () => {
      // 페이지가 숨겨질 때도 비디오 정지
      if (document.hidden) {
        try {
          const videoElements = document.querySelectorAll('video');
          videoElements.forEach((video) => {
            try {
              if (video && !video.paused) {
                video.pause();
              }
            } catch (error) {
              // null pointer 오류 방지 - 무시하고 계속 진행
            }
          });
        } catch (error) {
          console.warn('페이지 숨김 시 비디오 정지 중 오류:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleBeforeUnload); // 추가 안전장치
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 컴포넌트 언마운트 시에도 볼륨 상태 초기화
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearVolumeState(); // 다른 메뉴로 이동할 때도 초기화
    };
  }, []);

  // 최초 URL 저장 (컴포넌트 마운트 시 한 번만)

  useEffect(() => {
    if (!initialUrlRef.current) {
      initialUrlRef.current = location.pathname;

      // 즉시 pinnedVideo ID 추출해서 로그 확인
      const detailPathPattern = Paths.getPath('shortsMediaDetail').replace(':shortsMediaId', '');

      let pinnedFirstVideoShortsMediaId = '';
      if (initialUrlRef.current.includes(detailPathPattern)) {
        pinnedFirstVideoShortsMediaId = initialUrlRef.current
          .replace(detailPathPattern, '')
          .split('?')[0]
          .split('/')[0];
      } else {

        // 대안으로 직접 패턴 매칭 시도
        const urlParts = initialUrlRef.current.split('/');

        // /contents/shorts-media/detail/SHTM... 형태라면
        if (urlParts.includes('detail') && urlParts.includes('shorts-media')) {
          const detailIndex = urlParts.indexOf('detail');
          if (detailIndex !== -1 && urlParts[detailIndex + 1]) {
            pinnedFirstVideoShortsMediaId = urlParts[detailIndex + 1].split('?')[0];
          }
        }
      }
    }
  }, []);

  // 통합된 pinnedVideo 처리 로직

  useEffect(() => {
    // 이미 처리되었으면 스킵
    if (isPinnedVideoProcessedRef.current) {
      return;
    }

    // 최초 URL 기준으로 shortsMediaId 추출
    const targetPath = initialUrlRef.current || location.pathname;

    let pinnedFirstVideoShortsMediaId = '';

    // 대안 방법: URL을 분할해서 detail 뒤의 ID 추출 (더 안전함)
    const urlParts = targetPath.split('/');
    if (urlParts.includes('detail') && urlParts.includes('shorts-media')) {
      const detailIndex = urlParts.indexOf('detail');
      if (detailIndex !== -1 && urlParts[detailIndex + 1]) {
        pinnedFirstVideoShortsMediaId = urlParts[detailIndex + 1].split('?')[0];
      }
    }

    if (pinnedFirstVideoShortsMediaId && pinnedFirstVideoShortsMediaId.length > 0) {
      // displayedVideos가 로드되지 않았으면 대기
      if (displayedVideos.length === 0) {
        return;
      }

      // displayedVideos에서 pinnedVideo 찾기
      const existingVideo = displayedVideos.find(video =>
        video.shortsMediaId === pinnedFirstVideoShortsMediaId
      );

      if (existingVideo) {
        // 기존 영상을 맨 앞으로 이동
        setDisplayedVideos((prevState) => {
          // 이미 첫 번째에 있으면 변경하지 않음
          if (prevState[0]?.shortsMediaId === pinnedFirstVideoShortsMediaId) {
            return prevState;
          }

          const filteredPrevState = prevState.filter(video =>
            video.shortsMediaId !== pinnedFirstVideoShortsMediaId
          );
          const newVideos = [existingVideo, ...filteredPrevState];

          return newVideos;
        });

        setCurrentVideoIndex(0);
        
        // ✅ pinnedVideo는 항상 가상화 범위에 포함되도록 강제 스크롤
        setTimeout(() => {
          scrollToVideo(0);
          // ✅ 추가: 첫 번째 비디오가 확실히 로드되도록 강제 트리거
          const firstVideoElement = videoRefs.current[0];
          if (firstVideoElement) {
            // Intersection Observer 트리거를 위한 강제 스크롤
            firstVideoElement.scrollIntoView({ 
              behavior: 'auto', // smooth 대신 auto로 즉시 이동
              block: 'center' 
            });
          }
        }, 100);
        
        setPinnedShortsMediaDetailContents(existingVideo);
        isPinnedVideoProcessedRef.current = true;

      } else {
        // displayedVideos에 없으면 API 호출
        setIsLoadingPinnedVideo(true);
        fetchShortsMediaDetail(pinnedFirstVideoShortsMediaId).then((res) => {
          if (res?.resultCode !== 200) {
            console.warn('🔥 API 호출 실패:', res);
            setPinnedShortsMediaDetailContents(null);
            isPinnedVideoProcessedRef.current = true;
            return;
          }
          setPinnedShortsMediaDetailContents(res);
          isPinnedVideoProcessedRef.current = true;
        }).catch((error) => {
          console.error('🔥 pinnedShortsMediaDetail 로드 실패:', error);
          setPinnedShortsMediaDetailContents(null);
          isPinnedVideoProcessedRef.current = true;
        }).finally(() => {
          setIsLoadingPinnedVideo(false);
        });
      }
    } else {
      isPinnedVideoProcessedRef.current = true;
    }
  }, [displayedVideos, fetchShortsMediaDetail, scrollToVideo]);

  // displayedVideos ref 업데이트
  useEffect(() => {
    displayedVideosRef.current = displayedVideos;
  }, [displayedVideos]);

  // 컴포넌트 마운트시 데이터 로딩 - 최적화된 버전
  useEffect(() => {
    if (!isInitialLoadRef.current) return;

    // // 개발환경에서 메모리 모니터링 시작
    // if (process.env.NODE_ENV === 'development') {
    //   const memoryInterval = setInterval(() => {
    //     if ('memory' in performance) {
    //       const memory = (performance as any).memory;
    //       const videos = document.querySelectorAll('video').length;
    //
    //       // ✅ 빠른 스크롤 시에는 더 관대한 임계값 적용
    //       const videoThreshold = isFastScrolling ? 30 : 20; // 범위 확대로 인한 임계값 상향 조정
    //
    //       if (memory.usedJSHeapSize > 100 * 1024 * 1024) {
    //         console.warn('⚠️ 메모리 사용량이 100MB를 초과했습니다!');
    //       }
    //
    //       if (videos > videoThreshold) {
    //         console.warn(`⚠️ 비디오 요소가 ${videoThreshold}개를 초과했습니다! 가상화 로직 검토 필요`);
    //       }
    //     }
    //   }, 10000);
    //
    //   // cleanup 시 인터벌 정리
    //   const originalCleanup = () => {
    //     clearInterval(memoryInterval);
    //   };
    //
    //   // cleanup 함수를 나중에 호출할 수 있도록 저장
    //   (window as any).shortsMemoryCleanup = originalCleanup;
    // }

    const loadData = async () => {
      try {
        isInitialLoadRef.current = false;
        const result = await fetchShortsMediaList();
        // API 호출은 성공했지만 데이터가 없는 경우
        if (!result || result.length === 0) {
          // 빈 배열이 이미 shortsMediaList에 설정되어 있으므로 다음 useEffect에서 처리됨
        }
      } catch (error) {
        console.error('숏폼 데이터 로딩 실패:', error);
        // 에러 발생 시 빈 상태로 설정
        setDisplayedVideos([]);
        setHasMoreVideos(false);
        setCurrentVideoIndex(0);
        videoRefs.current = [];
      }
    };

    loadData();
  }, [fetchShortsMediaList]);


  // shortsMediaIds가 변경되면 초기 비디오 상세 정보를 로드
  useEffect(() => {
    if (shortsMediaIds && shortsMediaIds.length > 0) {
      const loadInitialVideos = async () => {
        try {
          // 초기 5개의 비디오 상세 정보만 로드
          const initialIds = shortsMediaIds.slice(0, INITIAL_LOAD_COUNT);
          const initialVideos = await fetchShortsMediaDetailsBatch(initialIds);

          if (initialVideos.length > 0) {
            // shortsMediaId가 있으면 해당 영상을 최상단에 배치
            const sortedVideoList = sortVideosByPriority(initialVideos);

            // pinnedVideo가 처리되지 않은 경우에만 URL 업데이트 (일반 메뉴 클릭 시)
            if (!isPinnedVideoProcessedRef.current && sortedVideoList[0]?.shortsMediaId) {
              // 현재 URL에 detail ID가 있는지 확인
              const currentPath = location.pathname;
              const hasDetailId = currentPath.includes('/detail/') && currentPath.includes('shorts-media');

              if (!hasDetailId) {
                // detail ID가 없으면 첫 번째 영상으로 URL 업데이트
                const detailPath = Paths.getPath('shortsMediaDetail').replace(':shortsMediaId', sortedVideoList[0].shortsMediaId);
                window.history.replaceState(null, '', detailPath);

                // pinnedVideo가 없는 일반 접근이므로 처리 완료로 표시
                isPinnedVideoProcessedRef.current = true;
              }
            }

            setDisplayedVideos(sortedVideoList);
            setHasMoreVideos(shortsMediaIds.length > INITIAL_LOAD_COUNT);
            setCurrentVideoIndex(0);
            // videoRefs 배열 초기화
            videoRefs.current = new Array(sortedVideoList.length).fill(null);
          } else {
            setDisplayedVideos([]);
            setHasMoreVideos(false);
            setCurrentVideoIndex(0);
            videoRefs.current = [];
          }
        } catch (error) {
          console.error('초기 비디오 로드 실패:', error);
          setDisplayedVideos([]);
          setHasMoreVideos(false);
          setCurrentVideoIndex(0);
          videoRefs.current = [];
        }
      };

      loadInitialVideos().then(() => {});
    } else if (shortsMediaIds && shortsMediaIds.length === 0) {
      // API 호출 성공했지만 데이터가 없는 경우
      setDisplayedVideos([]);
      setHasMoreVideos(false);
      setCurrentVideoIndex(0);
      videoRefs.current = [];
    }
  }, [shortsMediaIds, INITIAL_LOAD_COUNT, sortVideosByPriority, fetchShortsMediaDetailsBatch]);

  // API로 새로 가져온 pinnedShortsMediaDetailContents를 displayedVideos에 추가
  useEffect(() => {
    if (!pinnedShortsMediaDetailContents) return;

    const currentDisplayedVideos = displayedVideosRef.current;

    // displayedVideos가 비어있으면 pinnedVideo만으로 배열 생성
    if (currentDisplayedVideos.length === 0) {

      setDisplayedVideos([pinnedShortsMediaDetailContents]);
      setCurrentVideoIndex(0);

      // URL 업데이트
      const currentShortsMediaId = location.pathname.split('/').pop()?.replace(/\?.*$/, '');
      if (currentShortsMediaId !== pinnedShortsMediaDetailContents.shortsMediaId) {
        const detailPath = Paths.getPath('shortsMediaDetail').replace(':shortsMediaId', pinnedShortsMediaDetailContents.shortsMediaId);
        window.history.replaceState(null, '', detailPath);
      }

      // 첫 번째 비디오로 스크롤
      setTimeout(() => {
        scrollToVideo(0);
      }, 100);

      return;
    }

    // 이미 displayedVideos에 있는지 확인 (중복 방지)
    const existingVideo = currentDisplayedVideos.find(video =>
      video.shortsMediaId === pinnedShortsMediaDetailContents.shortsMediaId
    );

    // 이미 있으면 아무것도 하지 않음 (통합 useEffect에서 이미 처리됨)
    if (existingVideo) {
      return;
    }

    setDisplayedVideos((prevState) => {
      // API로 새로 가져온 영상을 맨 앞에 추가
      const newVideos = [pinnedShortsMediaDetailContents, ...prevState] as ShortsMediaContentsDetailResDto[];

      // URL 업데이트
      if (newVideos.length > 0 && newVideos[0]?.shortsMediaId) {
        const currentShortsMediaId = location.pathname.split('/').pop()?.replace(/\?.*$/, '');
        if (currentShortsMediaId !== newVideos[0].shortsMediaId) {
          const detailPath = Paths.getPath('shortsMediaDetail').replace(':shortsMediaId', newVideos[0].shortsMediaId);
          window.history.replaceState(null, '', detailPath);
        }
      }

      return newVideos;
    });

    // 현재 비디오 인덱스를 0으로 설정 (첫 번째 비디오로 이동)
    setCurrentVideoIndex(0);

    // 첫 번째 비디오로 스크롤
    setTimeout(() => {
      scrollToVideo(0);
    }, 100);

  }, [pinnedShortsMediaDetailContents, scrollToVideo]);

  // displayedVideos가 변경되면 videoRefs 배열 크기 조정 - 최적화
  useEffect(() => {
    const currentLength = videoRefs.current.length;
    const newLength = displayedVideos.length;

    if (newLength > currentLength) {
      // 새 비디오가 추가된 경우 videoRefs 배열 확장
      const additionalRefs = new Array(newLength - currentLength).fill(null);
      videoRefs.current = [...videoRefs.current, ...additionalRefs];
    } else if (newLength < currentLength) {
      // 비디오가 줄어든 경우 배열 크기 조정
      videoRefs.current = videoRefs.current.slice(0, newLength);
    }
  }, [displayedVideos.length]); // displayedVideos 대신 length만 감시하여 최적화

  const handleLike = useCallback(async (videoId: string, shortsMediaId: string) => {
    try {

      // 현재 비디오 정보 찾기
      const currentVideoIndex = displayedVideos.findIndex(video => video.shortsMediaId === shortsMediaId);
      const currentVideo = displayedVideos[currentVideoIndex];

      // 이미 좋아요한 상태인지 확인
      if (currentVideo?.hasLikedYn === 'Y') {
        return;
      }

      await likeShortsMedia(shortsMediaId);

      // API 호출 성공 시 로컬 상태 업데이트
      if (currentVideoIndex !== -1) {
        setDisplayedVideos(prev => {
          const updated = [...prev];
          updated[currentVideoIndex] = {
            ...updated[currentVideoIndex],
            likeCnt: (updated[currentVideoIndex].likeCnt || 0) + 1,
            hasLikedYn: 'Y'
          };
          return updated;
        });
      }
    } catch (error) {
      console.error('좋아요 실패:', error);
    }
  }, [likeShortsMedia, displayedVideos]);

  const handleShare = useCallback(async (videoId: string, shortsMediaId: string) => {
    try {
      // 공유 팝업 열기
      setSharePopup(true);
    } catch (error) {
      console.error('공유 실패:', error);
    }
  }, []);

  const handlePip = useCallback((videoId: string) => {
  }, []);

  // 현재 비디오의 댓글 데이터 로드
  const currentVideo = displayedVideos[currentVideoIndex];
  const currentShortsMediaId = currentVideo?.shortsMediaId;

  const {
    data: commentData,
    resultCode: commentResultCode,
    likeComment,
    modifyComment,
    deleteComment,
    mutate: commentMutate
  } = useCommentList(
    currentShortsMediaId || '',
    CommentPostType.SHORTS_MEDIA
  );

  // 댓글 데이터 처리
  const commentsData: ShortsCommentData[] = React.useMemo(() => {
    if (!commentData || commentResultCode !== 200) {
      // API 데이터가 없는 경우 빈 배열 반환
      return [];
    }

    const result: ShortsCommentData[] = [];
    const addedCommentIds = new Set<string>(); // 중복 방지를 위한 Set

    // 고정 댓글 추가
    if (commentData.fixedComment) {
      const fixedComment = convertCommentToShortsCommentData(commentData.fixedComment, userInfo);
      const commentWithReply = {
        ...fixedComment,
        replyCount: commentData.fixedComment.replyCnt || 0
      };
      result.push(commentWithReply);
      addedCommentIds.add(fixedComment.id);
    }

    // 베스트 댓글 추가 (고정 댓글과 중복되지 않는 것만)
    if (commentData.bestCommentList) {
      const bestComments = commentData.bestCommentList
        .filter(comment => !addedCommentIds.has(comment.commentId)) // 중복 제거
        .map((comment, index) => {
          const converted = convertCommentToShortsCommentData(comment, userInfo);
          addedCommentIds.add(converted.id);
          return {
            ...converted,
            type: 'best' as const,
            bestRank: index + 1,
            replyCount: comment.replyCnt || 0
          };
        });
      result.push(...bestComments);
    }

    // 일반 댓글 추가 (고정/베스트 댓글과 중복되지 않는 것만)
    if (commentData.commentList) {
      const normalComments = commentData.commentList
        .filter(comment => !addedCommentIds.has(comment.commentId)) // 중복 제거
        .map(comment => {
          const converted = convertCommentToShortsCommentData(comment, userInfo);
          addedCommentIds.add(converted.id);
          return {
            ...converted,
            replyCount: comment.replyCnt || 0
          };
        });
      result.push(...normalComments);
    }

    return result;
  }, [commentData, commentResultCode, userInfo]);

  // 댓글 총 개수
  const totalCommentCount = commentData?.totalCommentCnt || 0;

  const handleCommentLike = useCallback(async (commentId: string) => {
    try {
      await likeComment(commentId);
    } catch (error) {
      // 댓글 좋아요 실패 시 무시
    }
  }, [likeComment]);

  // useCommentReply 훅 - 선택된 댓글에 대해서만 초기화
  const { likeReply, modifyReply, deleteReply } = useCommentReply(selectedCommentId);

  const handleCommentReply = useCallback((commentId: string) => {
    setSelectedCommentId(commentId);
    setIsReplyInputOpen(prev => {
      const newState = {
        ...prev,
        [commentId]: !prev[commentId]
      };
      return newState;
    });

    // 답글 목록 로드
    if (!replyLists[commentId]) {
      import('../../../services').then(({ endpoints, fetcher }) => {
        fetcher.comment.getReplyComment(`${endpoints.comment.comment}/${commentId}`).then(res => {
          if (res.resultCode === 200) {
            setReplyLists(prev => ({
              ...prev,
              [commentId]: res.commentReplyList as CommentResDto[]
            }));
          }
        }).catch((error) => {
          // 답글 로드 실패 처리
          console.error('답글 로드 실패:', error);
        });
      });
    }
  }, [replyLists]);

  const handleReplyUpdate = useCallback((commentId: string) => {
    // 답글 목록 새로고침
    import('../../../services').then(({ endpoints, fetcher }) => {
      fetcher.comment.getReplyComment(`${endpoints.comment.comment}/${commentId}`).then(res => {
        if (res.resultCode === 200) {
          setReplyLists(prev => ({
            ...prev,
            [commentId]: res.commentReplyList as CommentResDto[]
          }));
          commentMutate(); // 전체 댓글 목록도 새로고침
        }
      }).catch((error) => {
        // 답글 새로고침 실패 처리
        console.error('답글 새로고침 실패:', error);
      });
    });
  }, [commentMutate]);

  const handleReplyLike = useCallback(async (replyId: string, commentId: string) => {
    try {
      await likeReply(replyId);
      handleReplyUpdate(commentId);
    } catch (error) {
      // 답글 좋아요 실패 처리
    }
  }, [likeReply, handleReplyUpdate]);

  const handleShowReplies = useCallback((commentId: string) => {

    setIsReplyOpen(prev => {
      const newState = {
        ...prev,
        [commentId]: !prev[commentId]
      };
      return newState;
    });

    // 답글 목록 로드 (답글이 열리는 경우에만)
    if (!isReplyOpen[commentId] && !replyLists[commentId]) {
      import('../../../services').then(({ endpoints, fetcher }) => {
        fetcher.comment.getReplyComment(`${endpoints.comment.comment}/${commentId}`).then(res => {
          if (res.resultCode === 200) {
            setReplyLists(prev => {
              const newState = {
                ...prev,
                [commentId]: res.commentReplyList as CommentResDto[]
              };
              return newState;
            });
          }
        }).catch((error) => {
          // 답글 로드 실패 처리
          console.error('답글 로드 실패:', error);
        });
      });
    }
  }, [isReplyOpen, replyLists]);

  const handleSetReplyList = useCallback((commentId: string, replyList: CommentResDto[]) => {
    setReplyLists(prev => ({
      ...prev,
      [commentId]: replyList
    }));
  }, []);

  // 댓글 수정 핸들러
  const handleCommentModify = useCallback(async (commentId: string, newContent: string) => {
    try {
          const modifyParams: CommentModifyReqDto = {
            commentId,
            contentsId: currentShortsMediaId,
            contentsType: CommentPostType.SHORTS_MEDIA,
            contents: newContent,
            contentsText: newContent.replace(/<[^>]*>/g, ''), // HTML 태그 제거
            bestCommentYn: 'N'
          };

          await modifyComment(modifyParams);

          commentMutate(); // 댓글 목록 새로고침
      } catch (error) {
          console.error('댓글 수정 실패:', error);
      }
  }, [currentShortsMediaId, modifyComment, commentMutate]);

  // 댓글 삭제 핸들러
  const handleCommentDelete = useCallback(async (commentId: string) => {
    try {
      const deleteParams = {
        commentId,
        contentsId: currentShortsMediaId,
        contentsType: CommentPostType.SHORTS_MEDIA
      };

      await deleteComment(deleteParams);
      commentMutate(); // 댓글 목록 새로고침
          } catch (error) {
        console.error('댓글 삭제 실패:', error);
      }
  }, [currentShortsMediaId, deleteComment, commentMutate]);

  // 답글 수정 핸들러
  const handleReplyModify = useCallback(async (replyId: string, commentId: string, newContent: string) => {
    try {
      // 선택된 댓글 ID 설정 후 modifyReply 사용
      setSelectedCommentId(commentId);

      const modifyParams: CommentModifyReqDto = {
        commentId: replyId,
        contentsId: currentShortsMediaId,
        contentsType: CommentPostType.SHORTS_MEDIA,
        contents: newContent,
        contentsText: newContent.replace(/<[^>]*>/g, ''), // HTML 태그 제거
        bestCommentYn: 'N'
      };

      await modifyReply(modifyParams);
      handleReplyUpdate(commentId); // 답글 목록 새로고침
          } catch (error) {
        console.error('답글 수정 실패:', error);
      }
  }, [currentShortsMediaId, handleReplyUpdate, modifyReply]);

  // 답글 삭제 핸들러
  const handleReplyDeleteHandler = useCallback(async (replyId: string, commentId: string) => {
    try {
      // 선택된 댓글 ID 설정 후 deleteReply 사용
      setSelectedCommentId(commentId);

      const deleteParams = {
        commentId: replyId,
        contentsId: currentShortsMediaId,
        contentsType: CommentPostType.SHORTS_MEDIA
      };

      await deleteReply(deleteParams);
      handleReplyUpdate(commentId); // 답글 목록 새로고침
          } catch (error) {
        console.error('답글 삭제 실패:', error);
      }
  }, [currentShortsMediaId, handleReplyUpdate, deleteReply]);

  /**
   * 풀영상 상세 정보 불러오기
   * @param mediaId 미디어 ID
   */
  const loadFullVersionInfo = async (mediaId: string) => {
    if (!mediaId || mediaId.trim().length === 0) {
      setFullVersionInfo(null);
      return;
    }

    try {
      setIsLoadingFullVersion(true);

      const res = await getFullVersionContentsDetailInfo(mediaId);

      if (!res || res.resultCode !== 200) {
        setFullVersionInfo(null);
        return;
      }

      setFullVersionInfo(res);
    } catch (error) {
      console.error('풀버전 영상 정보 로드 실패:', error);
      setFullVersionInfo(null);
    } finally {
      setIsLoadingFullVersion(false);
    }
  };

  /**
   * 풀영상 보러가기 클릭(a 태그 외 다른 영역 클릭)
   * @param mediaId
   */
  const moveToFullVersionMediaLink = (mediaId: string) => {
    if (!mediaId) return;
    if (!fullVersionMediaRef?.current) return;
    if (!fullVersionInfo) return;
    fullVersionMediaRef.current.click();
  }


  // 사용자 정보 초기화
  useEffect(() => {
    if (myInfoContext) setUserInfo(myInfoContext);
  }, [myInfoContext]);

  // 사용자 상세 정보 API Response 에서 가져오기
  useEffect(() => {
    if (myDetailInfo) setUserDetailInfo(myDetailInfo);
  }, [myInfoContext]);

  // 이모지 클릭 핸들러
  const handleEmojiClick = useCallback((emoji: string) => {
    // MentionAddComment 컴포넌트의 contentEditable div에 이모지 추가
    const commentDiv = cmtDivRef.current;
    if (commentDiv) {
      // 현재 내용 가져오기
      const currentContent = commentDiv.innerHTML || '';

      // 이모지 추가
      const newContent = currentContent + emoji;
      commentDiv.innerHTML = newContent;

      // React의 상태도 업데이트
      setCommentContent(newContent);

      // contentEditable div에 input 이벤트 디스패치하여 React가 변경을 감지하도록 함
      const event = new Event('input', { bubbles: true });
      commentDiv.dispatchEvent(event);

      // 커서를 텍스트 끝으로 이동
      commentDiv.focus();

      // contentEditable에서 커서를 끝으로 이동하는 방법
      const range = document.createRange();
      const selection = window.getSelection();
      if (selection) {
        range.selectNodeContents(commentDiv);
        range.collapse(false); // 끝으로 이동
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }, []);

  // 공유 팝업이 열렸을 때 네비게이션 숨기기/보이기 처리
  useEffect(() => {
    const navElement = document.querySelector('.nav');
    if (navElement) {
      if (sharePopup) {
        navElement.setAttribute('style', 'display: none');
      } else {
        navElement.setAttribute('style', '');
      }
    }
  }, [sharePopup]);

  /** 제목 높이 계산하여, 숏폼 영상 본문/우측 풀영상/댓글 영역 높이 지정 */
  useEffect(() => {
    if (!displayedVideos || displayedVideos.length === 0) return;
    if (!videoContainerRef.current) return;
    if (!rightContainerRef.current) return;
    if (!titleAreaRef.current || !titleAreaRef.current.clientHeight) return;

    titleAreaRef.current.style.height = `102px`;
    videoContainerRef.current.style.height = `calc(100% - 102px)`;
    rightContainerRef.current.style.marginTop = '102px';
  }, [location.pathname, displayedVideos]);

  // 풀버전 영상 정보 로드
  useEffect(() => {
    if (currentVideo?.mediaId) {
      loadFullVersionInfo(currentVideo.mediaId).then(() => {});
    } else {
      // 미디어 ID가 없으면 풀버전 정보 초기화
      setFullVersionInfo(null);
    }
  }, [currentVideo?.mediaId]);

  // 숏폼 공유하기 팝업 true시 숏폼 비디오 정지 false시 해제
  useEffect(() => {
    const videoElements = document.querySelectorAll('video');

    try {
      videoElements.forEach((video) => {
        try {
          if (!video) return;

          if (sharePopup && !video.paused) {
            video.pause();
          }

          if (!sharePopup && video.paused) {
            video.play();
          }
        } catch (error) {
          // 개별 video 오류 무시
        }
      });
    } catch (error) {
      console.warn(
        sharePopup
          ? '이메일 공유 시 비디오 정지 중 오류:'
          : '이메일 공유 팝업 닫은 후 재생 중 오류:',
        error
      );
    }
  }, [sharePopup]);

  // 현재 보이는 비디오 정보 (동적으로 변경)
  const displayTitle = currentVideo?.title || '로딩 중...';
  const displayViews = currentVideo?.readCnt || 0;
  const displayDate = currentVideo?.postDt ? dateYYYYMMDD(new Date(currentVideo.postDt)) : '';

  // 조건부 렌더링을 위한 상태 계산 - 최적화됨
  const isInitialLoading = isLoading && displayedVideos.length === 0 && !isLoadingPinnedVideo;
  const hasNoData = !isLoading && !isLoadingPinnedVideo && (!displayedVideos || displayedVideos.length === 0);

  // 초기 로딩 시에만 전체 스켈레톤 표시 (pinnedVideo 로딩은 제외)
  const shouldShowSkeleton = isInitialLoading;

  return (
    <>
      {shouldShowSkeleton && (
        <div className="content shorts">
          <div className="content_inner">
            <div className="section shorts-section">
              <div className="detail_left">
                <div className="detail_info is-small">
                  <div className="detail_info_con">
                    <div className="detail_info_title skeleton" style={{ height: '24px', borderRadius: '4px' }}></div>
                    <div className="detail_info_sub">
                      <span className="detail_info_views skeleton" style={{ height: '16px', width: '120px', borderRadius: '4px' }}></span>
                      <span className="detail_info_date skeleton" style={{ height: '16px', width: '80px', borderRadius: '4px' }}></span>
                    </div>
                  </div>
                </div>

                {/* 스켈레톤 비디오 영역 */}
                <div className="detail_content shorts-scroll-container">
                  <ul>
                    {[...Array(3)].map((_, index) => (
                      <li key={index} className="shorts-snap-item short-item">
                        <div className="detail_article">
                          <div className="detail_article_inner">
                            <div className="shorts_list_wrap">
                              <div className="detail_video skeleton" style={{ height: '400px', borderRadius: '8px' }}>
                                <div className="brightcove skeleton" style={{ height: '100%', borderRadius: '8px' }}></div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <ul className="detail_article_sub">
                          <li>
                            <div className="detail_info_link skeleton" style={{ height: '20px', width: '60px', borderRadius: '4px' }}></div>
                            <div className="detail_info_text skeleton" style={{ height: '16px', width: '40px', borderRadius: '4px' }}></div>
                          </li>
                          <li>
                            <div className="detail_info_link skeleton" style={{ height: '20px', width: '60px', borderRadius: '4px' }}></div>
                            <div className="detail_info_text skeleton" style={{ height: '16px', width: '40px', borderRadius: '4px' }}></div>
                          </li>
                        </ul>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* 스켈레톤 우측 영역 */}
              <div className="detail_right skeleton">
                <div className="detail_right_clip">
                  <div className="detail_right_title skeleton" style={{ height: '20px', borderRadius: '4px' }}></div>
                  <ul className="detail_relation">
                    <li>
                      <div className="detail_relation_link">
                        <div className="detail_relation_wrap">
                          <div className="relation_thumb skeleton" style={{ height: '120px', borderRadius: '8px' }}></div>
                          <div className="relation_desc">
                            <div className="relation_title skeleton" style={{ height: '18px', borderRadius: '4px' }}></div>
                            <div className="relation_sub is-pc">
                              <ul className="card_record">
                                <li className="skeleton" style={{ height: '16px', width: '60px', borderRadius: '4px' }}></li>
                                <li className="skeleton" style={{ height: '16px', width: '60px', borderRadius: '4px' }}></li>
                                <li className="skeleton" style={{ height: '16px', width: '60px', borderRadius: '4px' }}></li>
                              </ul>
                              <div className="relation_sub_date skeleton" style={{ height: '14px', width: '80px', borderRadius: '4px' }}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="detail_right_comment">
                  <div className="detail_right_comment_inner">
                    <div className="detail_comment_intro">
                      <span className="comment_title skeleton" style={{ height: '18px', width: '40px', borderRadius: '4px' }}></span>
                      <span className="comment_value skeleton" style={{ height: '18px', width: '60px', borderRadius: '4px' }}></span>
                    </div>
                    <div className="detail_comment_list">
                      <div className="comment_wrap">
                        <div className="comment_box">
                          {[...Array(6)].map((_, index) => (
                            <div key={index} className="comment_item">
                              <div className="comment_item_wrap">
                                <div className="comment_item_profile">
                                  <div className="profile skeleton" style={{ width: '40px', height: '40px', borderRadius: '50%' }}></div>
                                </div>
                                <div className="comment_item_con">
                                  <div className="comment_item_name">
                                    <div className="comment_item_name_link skeleton" style={{ height: '14px', width: '80px', borderRadius: '4px' }}></div>
                                    <span className="comment_item_time skeleton" style={{ height: '12px', width: '60px', borderRadius: '4px' }}></span>
                                  </div>
                                  <div className="comment_item_desc skeleton" style={{ height: '36px', borderRadius: '4px' }}></div>
                                  <div className="comment_item_sub">
                                    <div className="comment_item_likes skeleton" style={{ height: '14px', width: '50px', borderRadius: '4px' }}></div>
                                    <div className="comment_item_comment skeleton" style={{ height: '14px', width: '50px', borderRadius: '4px' }}></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasNoData && (
        <div className="content shorts">
          <div className="content_inner">
            <div className="section shorts-section">
              <div className="detail_left">
                <div className="detail_info is-small">
                  <div className="detail_info_con">
                    <div className="detail_info_title">숏폼 영상이 없습니다</div>
                    <div className="detail_info_sub">
                      <span className="detail_info_views">
                        현재 재생 가능한 숏폼 영상이 없습니다.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!shouldShowSkeleton && !hasNoData && (
        <>
          {/* CSS 애니메이션 스타일 추가 */}
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            
            @keyframes loadingPulse {
              0%, 100% { opacity: 0.6; }
              50% { opacity: 1; }
            }
            
            .loading-more-indicator {
              /* 스크롤 스냅에서 제외 */
              scroll-snap-align: none !important;
              /* 부드러운 표시를 위한 트랜지션 */
              transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
            }
            
            /* 새 영상 추가 시 부드러운 페이드 인 효과 */
            .shorts-snap-item {
              animation: fadeInUp 0.5s ease-out;
            }
            
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(30px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            
            /* 스켈레톤 애니메이션 개선 */
            .skeleton {
              animation: loadingPulse 1.5s ease-in-out infinite;
              background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
              background-size: 200% 100%;
              animation: skeletonLoading 1.5s infinite;
            }
            
            @keyframes skeletonLoading {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}</style>

        <div className="content_inner">
        {/* section */}
        <div className="section shorts-section">
          <div className="detail_left">
            <div className="detail_info is-small" ref={titleAreaRef}>
              <div className="detail_info_con">
                <div className="detail_info_title">
                  {displayTitle}
                </div>
                <div className="detail_info_sub">
                  <span className="detail_info_views">
                    조회수<span className="views">{displayViews.toLocaleString()}</span>회
                  </span>
                  <span className="detail_info_date">{displayDate}</span>
                </div>
              </div>
            </div>

            {/* 내부 내용은 영상 콘텐츠 영역 */}
            <div className="detail_content shorts-scroll-container" ref={videoContainerRef}>
              <ul>
                {displayedVideos.map((video, index) => (
                  <li
                    key={`${video.shortsMediaId}-${index}`}
                    ref={(el) => { videoRefs.current[index] = el; }}
                    data-index={index}
                    className="shorts-snap-item short-item"
                  >
                    <ShortsVideoItem
                      video={{
                        id: video.shortsMediaId,
                        videoId: video.videoId,
                        shortsMediaId: video.shortsMediaId,
                        likes: video.likeCnt || 0,
                        isLiked: video.hasLikedYn === 'Y'
                      }}
                      index={index}
                      currentVideoIndex={currentVideoIndex}
                      isAutoPlay={index === currentVideoIndex}
                      onLike={(videoId) => handleLike(videoId, video.shortsMediaId)}
                      onShare={(videoId) => handleShare(videoId, video.shortsMediaId)}
                      onPip={(videoId) => handlePip(videoId)}
                      onVideoEnd={handleVideoEnd}
                    />
                  </li>
                ))}

                {/* 추가 로딩 중일 때 부드러운 로딩 인디케이터 */}
                {isLoadingMore && (
                  <li className="loading-more-indicator" style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100px', // 고정된 작은 높이
                    padding: '20px',
                    background: 'transparent',
                    transition: 'all 0.3s ease-in-out',
                    opacity: 1,
                    transform: 'translateY(0)'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      fontSize: '14px',
                      color: '#666',
                      background: 'rgba(255, 255, 255, 0.9)',
                      padding: '16px 24px',
                      borderRadius: '30px',
                      backdropFilter: 'blur(10px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      border: '1px solid rgba(0, 0, 0, 0.05)'
                    }}>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        border: '2px solid #e0e0e0',
                        borderTop: '2px solid #007bff',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      <span style={{ fontWeight: 500 }}>새로운 영상 불러오는 중...</span>
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </div>

          {/* 영상 풀버전이 없을 경우 .no_clip 클래스 추가 */}
          {/* 풀버전 로딩 시에만 skeleton 클래스 추가 */}
          <div className={`detail_right ${isLoadingFullVersion ? 'skeleton' : ''} ${!currentVideo?.mediaId || !fullVersionInfo ? 'no_clip' : ''}`} ref={rightContainerRef}>
          {/* 영상 풀버전 보러가기 - mediaId가 있고 풀버전 정보가 성공적으로 로드되었을 때만 렌더링 */}
            {currentVideo?.mediaId && fullVersionInfo && fullVersionInfo.title && (
              <div className="detail_right_clip" onClick={() => moveToFullVersionMediaLink(currentVideo?.mediaId as string)} style={{ cursor: 'pointer' }}>
                <div className="detail_right_title">
                  영상 풀버전 보러가기 <img src="/assets/images/icon/ico-arrow-right-red.svg" alt="" />
                </div>
                <ul className="detail_relation">
                  <li>
                    <a ref={fullVersionMediaRef} href={Paths.getPath('videoDetail').replace(':mediaId',`${currentVideo?.mediaId}`)} target={"_blank"} className="detail_relation_link" onClick={(e) => e.stopPropagation()}> 
                      <div className="detail_relation_wrap">
                        <div className="relation_thumb">
                          <img
                            src={mediaThumbImageUri}
                            alt=""
                            className="relation_thumb_img"
                          />
                        </div>
                        <div className="relation_desc">
                          <div className="relation_title">
                            {fullVersionInfo?.title ? fullVersionInfo.title as string : ''}
                          </div>
                          <div className="relation_sub is-pc">
                            <ul className="card_record">
                              <li>
                                <img src="/assets/images/icon/ico-views.png" alt="views" />
                                {fullVersionInfo?.readCnt ? fullVersionInfo.readCnt.toLocaleString() : '0'}
                              </li>
                              <li>
                                <img src="/assets/images/icon/ico-comments.png" alt="comments" />
                                {fullVersionInfo?.commentCnt ? fullVersionInfo.commentCnt.toLocaleString() : '0'}
                              </li>
                              <li>
                                <img src="/assets/images/icon/ico-likes.png" alt="likes" />
                                {fullVersionInfo?.likeCnt ? fullVersionInfo.likeCnt.toLocaleString() : '0'}
                              </li>
                            </ul>
                            <div className="relation_sub_date">
                              {fullVersionInfo?.postDt ? dateYYYYMMDD(new Date(fullVersionInfo.postDt)) : ''}
                            </div>
                          </div>
                        </div>
                      </div>
                    </a>
                  </li>
                </ul>
              </div>
            )}

            {/* 영상 댓글 */}
            <div className="detail_right_comment shorts-comment-area">
              <div className="detail_right_comment_inner">
                <a href="#;" className="detail_comment_intro">
                  <span className="comment_title">댓글</span>
                  <span className="comment_value">
                    <span className="is-value">{totalCommentCount.toLocaleString()}</span>개
                  </span>
                </a>
                                  <div className="detail_comment_list shorts-comment-scrollable">
                  <div className="comment_wrap">
                    <div className="comment_box">
                      {commentsData.map((comment) => (
                        <ShortsCommentItem
                          key={comment.id}
                          comment={comment}
                          contentsId={currentShortsMediaId || ''}
                          onLike={handleCommentLike}
                          onReply={handleCommentReply}
                          onUpdate={() => {
                            // 댓글 목록 새로고침
                            commentMutate();
                          }}
                          commentSearchMailWidth={() => {}}
                          // 답글 관련 props 추가
                          isReplyInputOpen={isReplyInputOpen[comment.id] || false}
                          isReplyOpen={isReplyOpen[comment.id] || false}
                          replyList={replyLists[comment.id] || []}
                          onReplyLike={handleReplyLike}
                          onShowReplies={handleShowReplies}
                          onSetReplyList={handleSetReplyList}
                          // 수정/삭제 관련 props 추가
                          onModify={handleCommentModify}
                          onDelete={handleCommentDelete}
                          onReplyModify={handleReplyModify}
                          onReplyDelete={handleReplyDeleteHandler}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* 댓글 입력 */}
                <div className="detail_comment_input">
                  <div className="comment_con">
                    <div className="comment_emoji is-pc">
                      <ShortsMediaEmojiComment
                        clickEmoji={handleEmojiClick}
                        commentPostType={CommentPostType.SHORTS_MEDIA}
                        tags={''}
                        title={currentVideo?.title || ''}
                        category={''}
                        contentCreatorNm={''}
                      />
                    </div>
                    <div className="comment_form">
                      <div className="comment_profile">
                        <ProfileImage
                          hasRetire={userDetailInfo?.userStatus === 'RETIRE'}
                          name={userInfo?.name || ''}
                          score={userInfo?.actScore as number}
                          image={userInfo?.profileImage}
                          isAdmin={userInfo?.isAdmin}
                          isCommentList={true}
                        />
                      </div>
                      <div className="comment_input" ref={cmtInputRef}>
                        <MentionAddComment
                          contentsId={currentShortsMediaId || ''}
                          commentPostType={CommentPostType.SHORTS_MEDIA}
                          tags={''}
                          title={currentVideo?.title || ''}
                          category={''}
                          contentCreatorNm={''}
                          isReply={false}
                          cmtDivRef={cmtDivRef}
                          cmtInputRef={cmtInputRef}
                          commentSearchMailWidth={() => {}} // 필수 속성
                          update={() => {
                            // 댓글 목록 새로고침
                            commentMutate();
                            setCommentContent(''); // 댓글 작성 후 상태 초기화
                          }}
                          setReplyList={() => {
                            // 일반 댓글이므로 빈 함수
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 스켈레톤 로딩 UI - 풀버전 로딩 시에만 표시 */}
            {isLoadingFullVersion && (
              <div className="skeleton_wrap">
                <div className="detail_right_clip">
                  <div className="detail_right_title">
                    영상 풀버전 보러가기 <img src="/assets/images/icon/ico-arrow-right-red.svg" alt="" />
                  </div>
                  <ul className="detail_relation">
                    <li>
                      <a href="#;" className="detail_relation_link">
                        <div className="detail_relation_wrap">
                          <div className="relation_thumb"></div>
                          <div className="relation_desc">
                            <div className="relation_title"></div>
                            <div className="relation_desc_sub"></div>
                            <div className="relation_sub is-pc">
                              <ul className="card_record">
                                <li></li>
                                <li></li>
                              </ul>
                              <div className="relation_sub_date"></div>
                            </div>
                          </div>
                        </div>
                      </a>
                    </li>
                  </ul>
                </div>
                
                <div className="detail_right_comment">
                  <div className="detail_right_comment_inner">
                    <a href="#;" className="detail_comment_intro">
                      <span className="comment_title">댓글</span>
                      <span className="comment_value"></span>
                    </a>
                    <div className="detail_comment_list">
                      <div className="comment_wrap">
                        <div className="comment_box">
                          {[...Array(6)].map((_, index) => (
                            <div key={index} className="comment_item">
                              <div className="comment_item_wrap">
                                <div className="comment_item_profile">
                                  <div className="profile"></div>
                                </div>
                                <div className="comment_item_con">
                                  <div className="comment_item_name">
                                    <a href="#;" className="comment_item_name_link popup-profile"> </a>
                                    <span className="comment_item_time"></span>
                                  </div>
                                  <div className="comment_item_desc"></div>
                                  <div className="comment_item_sub">
                                    <a href="#;" className="comment_item_likes"></a>
                                    <a href="#;" className="comment_item_comment"></a>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* // section */}
        </div>
        </>
      )}
      
      {sharePopup && (
        <SharePopup
          onClose={() => setSharePopup(false)}
          title={currentVideo?.title || '숏폼 미디어'}
          contentsId={currentVideo?.shortsMediaId || ''}
          contentsPostType={'SHORTS_MEDIA'}
        />
      )}
    </>
  );
};

export default ShortsMediaList;
